import React, { useEffect } from "react";

function getResultModeLabel(resultMode, meta) {
  if (resultMode === "narrative") return meta.available;
  if (resultMode === "internal") return meta.internal;
  return meta.pending;
}

function getResultParagraphs(survey, surveyCopy) {
  if (survey.resultMode === "narrative") return surveyCopy.resultSummary || [];
  if (survey.resultMode === "internal") return surveyCopy.internalSummary || [];
  return surveyCopy.pendingSummary || [];
}

function SurveyResultsModal({
  isOpen,
  survey,
  surveyCopy,
  meta,
  modalCopy,
  actions,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("survey-modal-open");

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft" && hasPrev) onPrev();
      if (event.key === "ArrowRight" && hasNext) onNext();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("survey-modal-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, onPrev, onNext, hasPrev, hasNext]);

  if (!isOpen || !survey || !surveyCopy) return null;

  const resultParagraphs = getResultParagraphs(survey, surveyCopy);
  const statusLabel = getResultModeLabel(survey.resultMode, meta);

  return (
    <div
      className="survey-modal-overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="survey-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`survey-modal-title-${survey.id}`}
      >
        <button
          type="button"
          className="survey-modal__close"
          onClick={onClose}
          aria-label={actions.close}
        >
          <i className="fas fa-times" />
        </button>

        <div className="survey-modal__scroll">
          <div className="survey-modal__header">
            <div className="survey-modal__badges">
              <span className="survey-pill survey-pill--primary">
                {statusLabel}
              </span>
              <span className="survey-pill survey-pill--neutral">
                {meta.periodic}
              </span>
              {survey.isCommissionRecommendation && (
                <span className="survey-pill survey-pill--accent">
                  {meta.commission}
                </span>
              )}
            </div>

            <h2 id={`survey-modal-title-${survey.id}`}>{surveyCopy.title}</h2>
            <p>{surveyCopy.short}</p>
          </div>

          <div className="survey-modal__body">
            <div className="survey-modal__section">
              <h3>{modalCopy.purposeTitle}</h3>
              <p>{surveyCopy.purpose}</p>
            </div>

            <div className="survey-modal__section">
              <h3>{modalCopy.resultsTitle}</h3>
              {resultParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>

            <div className="survey-modal__section survey-modal__section--status">
              <h3>{modalCopy.statusTitle}</h3>
              <p>{surveyCopy.statusNote}</p>
            </div>
          </div>
        </div>

        <div className="survey-modal__footer">
          <button
            type="button"
            className="survey-btn survey-btn--ghost"
            onClick={onPrev}
            disabled={!hasPrev}
          >
            <i className="fas fa-arrow-left" />
            {actions.previous}
          </button>

          <button
            type="button"
            className="survey-btn survey-btn--primary"
            onClick={onNext}
            disabled={!hasNext}
          >
            {actions.next}
            <i className="fas fa-arrow-right" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default SurveyResultsModal;
